import React, { useEffect, useState, useCallback } from 'react';
import { Typography } from '@mui/material';
import { demoData } from '../../gameData/data';
import {
  getLastDayPlayed,
  isGameOver,
  getGameStateFromSetup,
  updateGameFinishedOnDate,
  getPlayerStreakStatistics,
  NUMBER_OF_HINTS,
} from '../../helpers';
import { getSolvedPuzzle } from '../../helpers/gameSolver';
import {
  saveGameToLocalStorage,
  loadGameFromLocalStorage,
  saveStatsToLocalStorage,
} from '../../helpers/localStorage';
import { GameState, GameWithDate } from '../../types/types';
import Game from '../game/Game';
import HelpModal from '../modals/HelpModal';
import InfoModal from '../modals/InfoModal';
import WinModal from '../modals/WinModal';
import NavBar from '../navBar/NavBar';
import Splash from '../splash/Splash';
import { StatisticsRequest } from '../../helpers/api';
import {
  useCurrentGame,
  usePostStatistics,
  useStatisticsForDate,
} from '../../helpers/useApi';

const INSTAGRAM_LINK = 'https://www.instagram.com/wormle.game/';

function HomePage() {
  // MODALS
  type ModalType = 'help' | 'info' | 'win' | null;
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const openModal = useCallback(
    (modal: ModalType) => setActiveModal(modal),
    [],
  );
  const closeModal = useCallback(() => setActiveModal(null), []);

  // GAME STATE
  type GameMode = 'splash' | 'demo' | 'real';
  const [mode, setMode] = useState<GameMode>('splash');
  const [gameState, setGameState] = useState<GameState>();
  const [gameSetupData, setGameSetupData] = useState<GameWithDate>();
  const [startTime, setStartTime] = useState<number>(0);
  const [shouldPostStatistics, setShouldPostStatistics] = useState(true);

  // DATA FETCHING
  const {
    data: fetchedGame,
    isLoading: isGameLoading,
    isError: isGameError,
    error: gameError,
  } = useCurrentGame();
  const postStatisticsMutation = usePostStatistics();
  const statisticsForDateMutation = useStatisticsForDate();

  useEffect(() => {
    const solvePuzzle = () => {
      if (gameSetupData?.game) {
        setShouldPostStatistics(false);
        setGameState(getSolvedPuzzle(gameSetupData.game).solvedGameState);
      }
    };
    window.Wormle = window.Wormle || {};
    window.Wormle.solvePuzzle = solvePuzzle;
  }, [gameSetupData]);

  useEffect(() => {
    loadGame();
  }, [fetchedGame, openModal]);

  useEffect(() => {
    if (gameState && isGameOver(gameState)) onWinGame();
  }, [gameState]);

  useEffect(() => {
    if (gameState && gameSetupData && mode === 'real') {
      saveGameToLocalStorage(gameSetupData.date, gameState, startTime);
    }
  }, [gameState]);

  function loadGame() {
    if (!fetchedGame) return;

    setGameSetupData(fetchedGame);

    if (!getLastDayPlayed()) openModal('help');

    const localGame = loadGameFromLocalStorage();

    if (!localGame || localGame.gameDate !== fetchedGame.date) {
      setGameState(getGameStateFromSetup(fetchedGame.game));
      setStartTime(Date.now());
      return;
    }

    setGameState(localGame.gameState);
    setStartTime(localGame.startDate);
  }

  const onPlayDemo = useCallback(() => {
    closeModal();
    setMode('demo');
    setGameState(getGameStateFromSetup(demoData.game));
  }, [closeModal]);

  const onPlayReal = useCallback(() => {
    setMode('real');
    loadGame();
  }, [fetchedGame]);

  const onWinGame = useCallback(async () => {
    if (!gameSetupData || !gameState) return;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    if (
      getLastDayPlayed() !== gameSetupData.date &&
      mode !== 'demo' &&
      shouldPostStatistics
    ) {
      updateGameFinishedOnDate(gameSetupData.date);
      await sendStatistic();
    }

    openModal('win');
  }, [gameState, gameSetupData, mode, shouldPostStatistics, openModal]);

  async function sendStatistic() {
    if (!gameSetupData || !gameState) return;

    const stats = getPlayerStreakStatistics();
    const seconds = (Date.now() - startTime) / 1000;

    const request: StatisticsRequest = {
      hintsUsed: NUMBER_OF_HINTS - gameState.hintsRemaining,
      moveCount: gameState.moveCount,
      secondsToComplete: seconds,
      streak: stats.currentStreak,
    };

    try {
      await postStatisticsMutation.mutateAsync({
        date: new Date(gameSetupData.date),
        statistics: request,
      });

      const comparativeStats = await statisticsForDateMutation.mutateAsync({
        date: new Date(gameSetupData.date),
        stats: request,
      });

      saveStatsToLocalStorage(
        gameSetupData.date,
        comparativeStats,
        seconds,
        NUMBER_OF_HINTS - gameState.hintsRemaining,
      );
    } catch (err) {
      console.error('Failed to post or fetch statistics:', err);
    }
  }

  if (isGameError)
    return (
      <Typography color="error">
        Error loading game: {gameError?.message}
      </Typography>
    );
  if (!gameSetupData || !gameState) return null;

  const isDemo = mode === 'demo';

  return (
    <>
      {mode === 'splash' && (
        <Splash
          gameDate={gameSetupData?.date}
          onStartDemoClick={onPlayDemo}
          onStartRealGameClick={onPlayReal}
        />
      )}
      {mode !== 'splash' && (
        <div className="App">
          <NavBar
            infoButtonOnClick={() => openModal('info')}
            helpButtonOnClick={() => openModal('help')}
          />

          <div className="page-body">
            <HelpModal
              isOpen={activeModal === 'help'}
              onClose={closeModal}
              tryDemoOnClick={onPlayDemo}
            />
            <InfoModal
              isOpen={activeModal === 'info'}
              onClose={closeModal}
              tryDemoOnClick={onPlayDemo}
            />

            {gameState && gameSetupData && (
              <WinModal
                isOpen={activeModal === 'win'}
                onClose={closeModal}
                seconds={(Date.now() - startTime) / 1000}
                gameState={gameState}
                theme={isDemo ? demoData.game.theme : gameSetupData.game.theme}
                date={gameSetupData.date}
                isDemo={isDemo}
                tryRealGameOnClick={onPlayReal}
                statsLoading={
                  postStatisticsMutation.isPending ||
                  statisticsForDateMutation.isPending
                }
                statsError={
                  postStatisticsMutation.isError ||
                  statisticsForDateMutation.isError
                }
                statsData={statisticsForDateMutation.data}
              />
            )}

            <GameArea />
          </div>

          <PageFooter />
        </div>
      )}
    </>
  );

  // SUBCOMPONENTS
  function GameArea() {
    if (!gameState) return null;

    return (
      <div>
        {isDemo && (
          <div className="demo-text">
            <Typography>
              Use the arrow keys or mouse to guide the worm and spell a related
              word on each row. Letters appear in the order shown at the bottom
              of the screen. If you make a mistake, tap the worm to move
              backwards, or hit &quot;reset tiles&quot;.
            </Typography>
          </div>
        )}

        <Game
          gameState={gameState}
          setGameState={setGameState}
          realGameOnClick={isDemo ? onPlayReal : undefined}
          resultsOnClick={() => openModal('win')}
        />

        {isDemo && (
          <div className="demo-text">
            <Typography>
              Can you figure out the theme? If you need some <b>direction</b>,
              use a hint to reveal a letter. If your motivation is going{' '}
              <b>South</b>, the &quot;<b>?</b>&quot; icon could be your{' '}
              <b>North</b> star.
            </Typography>
          </div>
        )}
      </div>
    );
  }
}

function PageFooter() {
  return (
    <footer className="page-footer padding">
      {/* <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer">
        Follow wormle on Instagram for daily solutions{' '}
        <InstagramIcon className="instagram-icon" fontSize="small" />
      </a> */}
    </footer>
  );
}

export default HomePage;
